/* eslint-disable no-shadow */
import { Timestamp, collection, doc, getDoc, getDocs, updateDoc } from "@firebase/firestore";
import { db } from "../../../utils/firebase";
import { amountToDeduct } from "./units";

export function calculatePantryPercentage(post, pantry) {
  // Get the total number of ingredients in the post
  const postCount = post.ingredients.length;

  // Get the number of matching ingredients in the pantry
  const pantryCount = post.ingredients.reduce((total, ingredient) => {
    const matchingIngredient = pantry.find(
      (pantryItem) =>
        ingredient.ingredientText.toLowerCase() ===
        pantryItem.pantryFoodTitle.toLowerCase() && ingredient.ingredientUnit === pantryItem.pantryUnit
    );
    if (matchingIngredient) {
      return total + 1;
    }
    return total;

  }, 0);

  // Calculate the percentage of pantry ingredients compared to the post
  const percentage = (pantryCount / postCount) * 100;

  return Math.round(percentage);
}

// Prep/descriptor words stripped when matching ingredient names to pantry items.
const DESCRIPTOR_WORDS = new Set([
  // Preparation methods
  'minced', 'chopped', 'sliced', 'diced', 'crushed', 'ground', 'grated',
  'peeled', 'cubed', 'cube', 'cubes', 'cooked', 'canned', 'raw',
  'julienned', 'shredded', 'beaten', 'smashed', 'bruised', 'halved',
  'quartered', 'wedged', 'wedge', 'wedges', 'segments', 'segment',
  'strips', 'strip', 'cut', 'into', 'pieces', 'piece', 'rings', 'ring',
  'fillet', 'fillets', 'slab', 'slabs', 'stalks', 'stalk', 'leaves',
  'leaf', 'cloves', 'clove', 'pounded', 'flat', 'scored', 'split',
  'drained', 'rinsed', 'separated', 'cleaned', 'deveined', 'scaled',
  // State / condition
  'fresh', 'dried', 'frozen', 'fried', 'deep', 'stir', 'crispy', 'crunchy',
  'toasted', 'roasted', 'baked', 'steamed', 'smoked', 'grilled',
  'leftover', 'cold', 'hot', 'warm', 'room', 'temp',
  // Size / amount descriptors
  'large', 'small', 'medium', 'thin', 'thick', 'whole', 'tiny', 'long',
  'boneless', 'bone-in', 'skin-on', 'skin', 'head-on',
  'fine', 'finely', 'thinly', 'smooth', 'roughly', 'coarsely', 'coarse',
  // Quality
  'organic', 'natural', 'unsalted', 'salted', 'ripe',
  // Recipe grammar
  'optional', 'for', 'frying', 'basting', 'serving', 'serve', 'serves',
  'garnish', 'garnishing', 'garnished', 'drizzling', 'dusting', 'brushing',
  'to', 'taste',
  'color', 'jar', 'style', 'mixed', 'with', 'and', 'or',
  'top', 'tails', 'on', 'off', 'quarters', 'quarter', 'cracked', 'boiling',
  // Cuts of meat — a pantry entry of the raw staple ("beef", "chicken")
  // should satisfy a recipe asking for a specific cut of it.
  'sirloin', 'rump', 'chuck', 'brisket', 'shank', 'tenderloin', 'flank',
  'round', 'loin', 'shoulder', 'breast', 'thigh', 'drumstick',
  'drumsticks', 'wing', 'wings', 'rib', 'ribs', 'ribeye', 'mince',
]);

// Alternate spellings normalized to one canonical form before matching.
const SPELLING_VARIANTS = {
  chili: 'chilli',
  chilies: 'chilli',
  yogurt: 'yoghurt',
  gray: 'grey',
  // So "minced X" and "X mince" share a token for specificity ranking
  // (both are descriptor-stripped in the main matcher, so this only affects
  // rawTokens / bestMatchIndex). Generic — works for any mince (beef, pork…).
  minced: 'mince',
};

// Ingredients treated as always on hand — no pantry entry required.
const ALWAYS_AVAILABLE = new Set(['water']);

function singularWord(w) {
  if (w.length > 3 && w.endsWith('es')) {
    const stem = w.slice(0, -2);
    const last = stem[stem.length - 1];
    // True double-suffix "-es" plurals (stem ends in s/x/z/ch/sh, or a
    // consonant+o): boxes->box, dishes->dish, glasses->glass, tomatoes->tomato.
    if (last === 's' || last === 'x' || last === 'z' || last === 'o'
        || stem.endsWith('ch') || stem.endsWith('sh')) {
      return stem;
    }
    // Otherwise "<word already ending in e>" + "s": flake+s, olive+s,
    // sauce+s, juice+s — stripping two chars broke these (flakes -> "flak").
    return w.slice(0, -1);
  }
  if (w.length > 2 && w.endsWith('s')) return w.slice(0, -1);
  return w;
}

function normalizeWords(text) {
  return (text || '')
    .toLowerCase()
    // Drop parenthetical notes/synonyms first: "spring onion (scallions)" →
    // "spring onion" (otherwise "scallions" survives as a stray token and the
    // ingredient looks more specific than the pantry item).
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !DESCRIPTOR_WORDS.has(w))
    .map(singularWord)
    .map((w) => SPELLING_VARIANTS[w] || w);
}

// Built-in aliases (always active). Firestore 'ingredientAliases' collection
// can add more at runtime via loadIngredientAliases().
const INGREDIENT_ALIASES = {
  'spinach leaves': 'spinach',
  'baby spinach': 'spinach',
  'rocket leaves': 'rocket',
  'arugula': 'rocket',
  'cilantro': 'coriander',
  'scallions': 'spring onion',
  'green onion': 'spring onion',
  'shallot': 'spring onion',
  'courgette': 'zucchini',
  'aubergine': 'eggplant',
  'capsicum': 'bell pepper',
  'prawn': 'shrimp',
  'prawns': 'shrimp',
};

// Merged map (built-ins + Firestore overrides). Updated by loadIngredientAliases().
let activeAliases = { ...INGREDIENT_ALIASES };

export async function loadIngredientAliases(db) {
  try {
    const { getDocs, collection } = await import('@firebase/firestore');
    const snap = await getDocs(collection(db, 'ingredientAliases'));
    const extra = {};
    snap.forEach((d) => {
      const { from, to } = d.data();
      if (from && to) extra[from.toLowerCase()] = to.toLowerCase();
    });
    activeAliases = { ...INGREDIENT_ALIASES, ...extra };
  } catch (e) {
    console.warn('loadIngredientAliases failed; using built-ins', e);
  }
}

function applyAliases(text) {
  const lower = (text || '').toLowerCase().trim();
  return activeAliases[lower] || text;
}

// Raw word set for specificity ranking — spelling-normalized + singularized
// but WITHOUT descriptor stripping, so "minced"/"mince" survive to tell a
// "beef mince" pantry item apart from a plain "beef".
function rawTokens(text) {
  return new Set((text || '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((w) => w)
    .map(singularWord)
    .map((w) => SPELLING_VARIANTS[w] || w));
}

// Among pantry titles that all match [ingredientText], returns the index of
// the most specific one (most shared raw words, fewest extra words) — so a
// recipe's "minced beef" deducts from "beef mince" rather than plain "beef".
// Mirrors the mobile IngredientMatcher.bestMatchIndex. Returns -1 if empty.
//
// Titles are alias-resolved before scoring — otherwise a custom alias like
// "beef scotch fillet" -> "beef" (used for a cut the built-in descriptor list
// doesn't recognize) only widened WHICH items count as candidates
// (titleMatchesIngredient), while this function still scored them on their
// literal, un-aliased words: "scotch"/"fillet" counted as unmatched "extra"
// words, so a same-named-but-EMPTY "beef" entry (0 extra words) always beat
// the aliased, actually-in-stock "beef scotch fillet" (2 extra words).
//
// [quantities], parallel to candidateTitles, is an optional tie-breaker: when
// two candidates score identically on words alone (as "beef" and an
// alias-resolved "beef scotch fillet" now do), prefer the one that's
// actually in stock, since a same-score choice that has zero of the item is
// never useful to the caller (checklist display or actual deduction).
export function bestMatchIndex(candidateTitles, ingredientText, quantities) {
  if (!candidateTitles || !candidateTitles.length) return -1;
  const ing = rawTokens(applyAliases(ingredientText));
  let bestIdx = 0;
  let bestOverlap = -1;
  let bestExtra = Infinity;
  let bestQty = -Infinity;
  for (let i = 0; i < candidateTitles.length; i += 1) {
    const t = rawTokens(applyAliases(candidateTitles[i]));
    let overlap = 0;
    t.forEach((w) => { if (ing.has(w)) overlap += 1; });
    const extra = t.size - overlap;
    const qty = quantities ? (parseFloat(quantities[i]) || 0) : 0;
    const better = overlap > bestOverlap
      || (overlap === bestOverlap && extra < bestExtra)
      || (overlap === bestOverlap && extra === bestExtra && qty > bestQty);
    if (better) {
      bestOverlap = overlap;
      bestExtra = extra;
      bestQty = qty;
      bestIdx = i;
    }
  }
  return bestIdx;
}

// True if [ingredientText] is something we never require the pantry to
// stock (currently just tap water).
export function isAlwaysAvailable(ingredientText) {
  const words = normalizeWords(ingredientText);
  return words.length === 1 && ALWAYS_AVAILABLE.has(words[0]);
}

function containsSubsequence(hay, needle) {
  if (!needle.length || needle.length > hay.length) return false;
  for (let i = 0; i + needle.length <= hay.length; i += 1) {
    let ok = true;
    for (let j = 0; j < needle.length; j += 1) {
      if (hay[i + j] !== needle[j]) { ok = false; break; }
    }
    if (ok) return true;
  }
  return false;
}

// Words that mark a derived product (e.g. "chicken broth", "garlic powder",
// "tomato sauce"). A raw staple shouldn't satisfy a derived ingredient.
const DERIVATIVE_QUALIFIERS = new Set([
  'broth', 'stock', 'powder', 'paste', 'puree', 'sauce', 'juice', 'extract',
]);

// True if the pantry contains the ingredient by name (prep/plural/unit agnostic).
// A derived ingredient only matches a staple sharing its qualifier
// (so "chicken broth" does not match the raw "chicken" staple).
// True if a single pantry/grocery item name satisfies the ingredient
// (normalized: ignores prep words, plurals, unit; derived products must share
// their qualifier so "garlic" matches "minced garlic" but not "garlic powder").
export function titleMatchesIngredient(itemTitle, ingredientText) {
  // Pantry titles listing alternative names ("prawn / shrimp",
  // "coriander / cilantro") match if the ingredient satisfies ANY of them —
  // mirrors the mobile IngredientMatcher.
  if ((itemTitle || '').includes('/')) {
    return itemTitle.split('/').some((part) => titleMatchesIngredient(part.trim(), ingredientText));
  }

  // Tap water — never require a pantry entry for it.
  if (isAlwaysAvailable(ingredientText)) return true;

  // Normalize, then re-apply aliases on the normalized form: "raw prawns"
  // only becomes "shrimp" after descriptor-stripping/singularizing exposes
  // the bare "prawn" — aliasing the raw string alone misses it, leaving
  // ingredient and title on different canonical names.
  const canonWords = (text) => {
    const words = normalizeWords(applyAliases(text));
    const aliased = activeAliases[words.join(' ')];
    return aliased ? normalizeWords(aliased) : words;
  };

  const ingWords = canonWords(ingredientText);
  if (!ingWords.length) return false;
  const titleWords = canonWords(itemTitle || '');
  if (!titleWords.length) return false;

  // Standard check: titleWords is a consecutive block inside ingWords.
  // Uncovered ingredient words must all be derivative qualifiers — otherwise
  // the ingredient is more specific (e.g. "chicken wings" ≠ pantry "chicken").
  if (containsSubsequence(ingWords, titleWords)) {
    let matchStart = 0;
    for (let i = 0; i + titleWords.length <= ingWords.length; i += 1) {
      const found = titleWords.every((w, j) => ingWords[i + j] === w);
      if (found) { matchStart = i; break; }
    }
    const uncovered = [
      ...ingWords.slice(0, matchStart),
      ...ingWords.slice(matchStart + titleWords.length),
    ];
    if (!uncovered.every((w) => DERIVATIVE_QUALIFIERS.has(w))) return false;
    const ingQualifiers = ingWords.filter((w) => DERIVATIVE_QUALIFIERS.has(w));
    const titleQualifiers = new Set(titleWords.filter((w) => DERIVATIVE_QUALIFIERS.has(w)));
    return ingQualifiers.every((q) => titleQualifiers.has(q));
  }

  // Brand/adjective-prefix check: ingredient words are a contiguous suffix of
  // title words. e.g. "Lurpak butter" → titleWords=["lurpak","butter"],
  // ingWords=["butter"] — suffix ["butter"] matches.
  if (titleWords.length > ingWords.length) {
    const suffix = titleWords.slice(titleWords.length - ingWords.length);
    if (suffix.join(' ') === ingWords.join(' ')) {
      const ingQualifiers = ingWords.filter((w) => DERIVATIVE_QUALIFIERS.has(w));
      const titleQualifiers = new Set(titleWords.filter((w) => DERIVATIVE_QUALIFIERS.has(w)));
      return ingQualifiers.every((q) => titleQualifiers.has(q));
    }
  }

  return false;
}

// Credit for a single ingredient, in [0, 1]. Mirrors the recipe-detail page's
// green-check / "?" / missing states so the tile % agrees with what the user
// sees there: full credit only when the pantry has enough (amountToDeduct
// converts the recipe amount into the pantry unit, with a raw fallback), a
// partial ratio when present-but-not-enough (the "?" case), and 0 when missing.
function ingredientCredit(ingredient, pantries) {
  if (isAlwaysAvailable(ingredient.ingredientText)) return 1;
  const candidates = (pantries || []).filter((p) =>
    titleMatchesIngredient(p.pantryFoodTitle, ingredient.ingredientText));
  if (!candidates.length) return 0;
  // Most-specific match (same as mobile) so the % reflects the pantry item
  // that actually gets deducted — e.g. "minced beef" uses "beef mince".
  const match = candidates[bestMatchIndex(
    candidates.map((p) => p.pantryFoodTitle), ingredient.ingredientText,
    candidates.map((p) => p.pantryQuantity),
  )];

  const reqQty = parseFloat(ingredient.ingredientQty);
  // No usable required quantity -> presence is enough.
  if (!reqQty || reqQty <= 0) return 1;

  const pantryQty = parseFloat(match.pantryQuantity) || 0;
  if (pantryQty <= 0) return 0;

  const needed = amountToDeduct(
    reqQty, ingredient.ingredientUnit, match.pantryUnit, ingredient.ingredientText,
  );
  // Can't determine a need -> treat presence as enough.
  if (!needed || needed <= 0) return 1;
  return Math.min(pantryQty / needed, 1);
}

export function percentCalculate(post, pantries) {
  const ingredients = post.ingredients || [];
  if (!ingredients.length || !ingredients[0].ingredientText) return '0%';

  let credit = 0;
  ingredients.forEach((ingredient) => {
    credit += ingredientCredit(ingredient, pantries);
  });

  const overall = (credit / ingredients.length) * 100;
  if (overall >= 100) return '100%';
  return `${Math.round(overall)}%`;
}

export async function getPost(postId, profileUid) {
  const postRef = doc(db, 'users', profileUid, 'posts', postId);
  const postInstance = await getDoc(postRef);
  return postInstance;
}



// eslint-disable-next-line import/prefer-default-export
export function logPost(post) {
  console.log(post);
}


export function ingredientInPantry(ingredient, pantry) {
  return pantry.some((pantryItem) =>
    titleMatchesIngredient(pantryItem.pantryFoodTitle, ingredient.ingredientText));
}


export async function getLikes(userId) {
  const likesRef = collection(db, "users", userId, "likes");
  const likesSnapshot = await getDocs(likesRef);
  return likesSnapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      docId: doc.id,
    };
  });
}

export async function addLike(userId, postId, authorId) {
  const likesRef = collection(db, "users", userId, "likes", postId);
  await updateDoc(likesRef, {
    postDocId: postId,
    profileUid: authorId,
    dateCreated: Timestamp.now(),
  })

}


export async function checkIfLiked(profileUid, postDocId, userUid) {
  const likeRef = doc(db, "users", profileUid, "posts", postDocId, "likes", userUid);

  const likeSnapshot = await getDoc(likeRef);

  return likeSnapshot.exists();
}